import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { useShorts } from "../../lib/queries";
import { useLang } from "../../lib/i18n";
import { toEmbed, ytThumbnail, getYouTubeId } from "../../lib/media";

const ShortCard = ({ item, index, pick }) => {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const id = getYouTubeId(item.video_url);
  const thumb = ytThumbnail(item.video_url);
  const embed =
    toEmbed(item.video_url) +
    `?autoplay=1&mute=${muted ? 1 : 0}&playsinline=1&loop=1&playlist=${id}&controls=1&modestbranding=1&rel=0`;

  return (
    <motion.div
      layout
      whileHover={{ y: -6 }}
      transition={{ duration: 0.35, ease: [0.2, 0.9, 0.2, 1] }}
      data-testid={`viral-card-${item.id}`}
      className="relative flex-shrink-0 snap-start w-[240px] md:w-[280px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/5 hover:border-[#A855F7]/60 group bg-black"
    >
      {playing ? (
        <>
          <iframe
            src={embed}
            title={item.title_tr}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
          <button
            data-testid={`viral-mute-${item.id}`}
            onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); setPlaying(false); setTimeout(() => setPlaying(true), 30); }}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center hover:bg-[#A855F7]/60"
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        </>
      ) : (
        <button
          onClick={() => setPlaying(true)}
          data-testid={`viral-play-${item.id}`}
          className="absolute inset-0 w-full h-full text-left"
        >
          {thumb ? (
            <img
              src={thumb}
              alt={item.title_tr || "Viral"}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#5B21B6]/40 to-[#101018]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:bg-[#A855F7] group-hover:border-[#A855F7] transition-colors">
              <Play size={22} fill="white" className="text-white ml-1" />
            </div>
          </div>
          <div className="absolute top-3 left-3 flex items-center gap-2 font-mono-ui text-[9px] tracking-widest uppercase text-white/80">
            <span className="px-2 py-1 rounded-full bg-red-500 text-white flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Shorts
            </span>
            <span className="px-2 py-1 rounded-full bg-black/60 border border-white/10">
              #{String(index + 1).padStart(2, "0")}
            </span>
          </div>
          <div className="absolute bottom-3 left-3 right-3 text-white">
            {item.brand_name && (
              <div className="text-[9px] font-mono-ui tracking-widest uppercase text-[#A855F7] mb-1">
                {item.brand_name}
              </div>
            )}
            <div className="text-sm font-medium leading-snug line-clamp-2">
              {pick(item, "title") || "Viral"}
            </div>
          </div>
        </button>
      )}
    </motion.div>
  );
};

export default function ViralWorks() {
  const { data: items = [] } = useShorts();
  const { t, lang, pick } = useLang();
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <section
      id="viral"
      data-testid="viral-section"
      className="py-32 relative overflow-hidden bg-[#0A0A11] border-y border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-12 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#A855F7]" />
            <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
              {lang === "tr" ? "Bölüm 07 — Viral" : "Chapter 07 — Viral"}
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white">
            {lang === "tr" ? "Viral İşler" : "Viral Works"}
          </h2>
          <p className="text-white/50 text-sm md:text-base mt-3 max-w-xl">
            {lang === "tr"
              ? "Milyonlarca izlenmeye ulaşan kısa formatlı prodüksiyonlarımız."
              : "Our short-form productions that reached millions of views."}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="viral-prev"
            onClick={() => scrollBy(-1)}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-[#A855F7]/60 hover:text-[#A855F7] transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            data-testid="viral-next"
            onClick={() => scrollBy(1)}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-[#A855F7]/60 hover:text-[#A855F7] transition-colors"
          >
            <ChevronRight size={16} />
          </button>
          <span className="font-mono-ui text-[10px] tracking-widest text-white/40 uppercase ml-3">
            {String(items.length).padStart(2, "0")} · Videos
          </span>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 md:gap-5 px-6 lg:px-10 pb-6 overflow-x-auto snap-x snap-mandatory scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((it, i) => (
          <ShortCard key={it.id} item={it} index={i} pick={pick} />
        ))}
        <div className="flex-shrink-0 w-2" />
      </div>
    </section>
  );
}
